import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { adminApi } from '../../lib/api';
import { useConfirm } from '../../components/ConfirmDialog';
import type { AdminApiKey, AdminOrg, AdminUser } from '../../types';

const PAGE_SIZE = 50;

type OrgRef = { _id: string; name: string; slug: string };
type UserRef = { _id: string; email: string; name: string };

function orgFromKey(k: AdminApiKey): OrgRef | null {
  if (!k.organizationId) return null;
  return typeof k.organizationId === 'object' ? (k.organizationId as OrgRef) : null;
}
function userFromKey(k: AdminApiKey): UserRef | null {
  if (!k.userId) return null;
  return typeof k.userId === 'object' ? (k.userId as UserRef) : null;
}

export function ApiKeysPage() {
  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [environment, setEnvironment] = useState<'' | 'live' | 'test'>('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'revoked'>('all');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<AdminApiKey | null>(null);
  const confirm = useConfirm();

  // Org typeahead — fetch all orgs lazily for the dropdown. Org count is small
  // enough (one row per org) that loading the full list once is fine.
  const { data: orgsData } = useSWR('admin/orgs:all', () =>
    adminApi.listOrgs({ limit: 500 })
  );
  const orgs: AdminOrg[] = orgsData?.organizations || [];

  const { data: usersData } = useSWR('admin/users:all', () =>
    adminApi.listUsers({ limit: 500 })
  );
  const users: AdminUser[] = usersData?.users || [];

  const params = useMemo(() => {
    const p: Parameters<typeof adminApi.listApiKeys>[0] = {
      limit: PAGE_SIZE,
      skip,
    };
    if (search.trim()) p.search = search.trim();
    if (orgFilter) p.orgId = orgFilter;
    if (userFilter) p.userId = userFilter;
    if (environment) p.environment = environment;
    if (activeFilter === 'active') p.active = true;
    if (activeFilter === 'revoked') p.active = false;
    return p;
  }, [search, orgFilter, userFilter, environment, activeFilter, skip]);

  const { data, mutate, isLoading } = useSWR(
    ['admin/api-keys', JSON.stringify(params)],
    () => adminApi.listApiKeys(params),
    { keepPreviousData: true },
  );

  const handleRevoke = async (k: AdminApiKey) => {
    const ownerEmail = userFromKey(k)?.email ?? 'unknown owner';
    const ok = await confirm({
      title: `Revoke '${k.name}'?`,
      message: `Any client using ${k.prefix}… (${ownerEmail}) will start getting 401s immediately. This cannot be undone.`,
      confirmLabel: 'Revoke key',
      danger: true,
    });
    if (!ok) return;
    await adminApi.revokeApiKey(k._id);
    mutate();
    setSelected(null);
  };

  const total = data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-100">API Keys</h1>
        <span className="text-xs text-gray-500">
          {isLoading ? 'Loading…' : `${total} key${total === 1 ? '' : 's'}`}
        </span>
      </div>

      {/* Filters */}
      <div className="bg-gray-900 border border-gray-800 rounded p-3 flex flex-wrap items-end gap-3">
        <Field label="Search" className="flex-1 min-w-48">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
            placeholder="Name or prefix…"
            className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100"
          />
        </Field>
        <Field label="Organization">
          <select
            value={orgFilter}
            onChange={(e) => { setOrgFilter(e.target.value); setSkip(0); }}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100 min-w-44"
          >
            <option value="">Any organization</option>
            {orgs.map((o) => (
              <option key={o._id} value={o._id}>{o.name}</option>
            ))}
          </select>
        </Field>
        <Field label="User">
          <select
            value={userFilter}
            onChange={(e) => { setUserFilter(e.target.value); setSkip(0); }}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100 min-w-44"
          >
            <option value="">Any user</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>{u.email}</option>
            ))}
          </select>
        </Field>
        <Field label="Environment">
          <select
            value={environment}
            onChange={(e) => { setEnvironment(e.target.value as '' | 'live' | 'test'); setSkip(0); }}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100"
          >
            <option value="">Any</option>
            <option value="live">live</option>
            <option value="test">test</option>
          </select>
        </Field>
        <Field label="Status">
          <select
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value as 'all' | 'active' | 'revoked'); setSkip(0); }}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
          </select>
        </Field>
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Prefix</th>
              <th className="px-4 py-2 font-medium">Owner</th>
              <th className="px-4 py-2 font-medium">Organization</th>
              <th className="px-4 py-2 font-medium">Env</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Last used</th>
              <th className="px-4 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {data?.apiKeys.map((k) => {
              const owner = userFromKey(k);
              const org = orgFromKey(k);
              return (
                <tr
                  key={k._id}
                  className="border-b border-gray-800 hover:bg-gray-800/40 cursor-pointer"
                  onClick={() => setSelected(k)}
                >
                  <td className="px-4 py-2 text-gray-100">{k.name}</td>
                  <td className="px-4 py-2"><code className="text-xs text-gray-300">{k.prefix}…</code></td>
                  <td className="px-4 py-2 text-gray-300 text-xs">
                    {owner?.email ?? <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-2 text-gray-300 text-xs">
                    {org ? (
                      <Link
                        to={`/admin/orgs/${org._id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-400 hover:underline"
                      >
                        {org.name}
                      </Link>
                    ) : (
                      <span className="text-gray-600">personal</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <Pill warn={k.environment === 'test'}>{k.environment}</Pill>
                  </td>
                  <td className="px-4 py-2">
                    {k.isActive ? <Pill ok>active</Pill> : <Pill>revoked</Pill>}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    {new Date(k.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
            {data && data.apiKeys.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No keys match these filters</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Prev</button>
            <button disabled={skip + PAGE_SIZE >= total} onClick={() => setSkip(skip + PAGE_SIZE)} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Next</button>
          </div>
        </div>
      )}

      {selected && (
        <KeyDrawer
          apiKey={selected}
          onClose={() => setSelected(null)}
          onRevoke={() => handleRevoke(selected)}
        />
      )}
    </div>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className ?? ''}`}>
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Pill({ children, ok, warn }: { children: React.ReactNode; ok?: boolean; warn?: boolean }) {
  const cls = ok
    ? 'bg-emerald-900/40 text-emerald-300'
    : warn
      ? 'bg-amber-900/40 text-amber-200'
      : 'bg-gray-800 text-gray-400';
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{children}</span>;
}

function KeyDrawer({
  apiKey,
  onClose,
  onRevoke,
}: {
  apiKey: AdminApiKey;
  onClose: () => void;
  onRevoke: () => void;
}) {
  const owner = userFromKey(apiKey);
  const org = orgFromKey(apiKey);
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div
        className="w-[28rem] bg-gray-900 border-l border-gray-800 p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-100">{apiKey.name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-200">×</button>
        </div>
        <div className="text-sm text-gray-400 space-y-1 mb-6">
          <div><code className="text-xs">{apiKey.prefix}…</code></div>
          <div className="text-xs">id: <code>{apiKey._id}</code></div>
          <div className="text-xs">environment: {apiKey.environment}</div>
          <div className="text-xs">status: {apiKey.isActive ? 'active' : `revoked${apiKey.revokedAt ? ` ${new Date(apiKey.revokedAt).toLocaleString()}` : ''}`}</div>
          <div className="text-xs">created: {new Date(apiKey.createdAt).toLocaleString()}</div>
          <div className="text-xs">last used: {apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt).toLocaleString() : '—'}</div>
        </div>

        <Section title="Owner">
          {owner ? (
            <div className="text-sm text-gray-300">
              {owner.email}
              {owner.name && <span className="text-gray-500"> · {owner.name}</span>}
            </div>
          ) : (
            <div className="text-xs text-gray-600 italic">User unknown (deleted?)</div>
          )}
        </Section>

        <Section title="Organization">
          {org ? (
            <Link to={`/admin/orgs/${org._id}`} className="text-sm text-blue-400 hover:underline">
              {org.name}
            </Link>
          ) : (
            <div className="text-xs text-gray-600 italic">Personal key (no org)</div>
          )}
        </Section>

        {apiKey.isActive && (
          <button
            onClick={onRevoke}
            className="w-full px-3 py-1.5 text-sm rounded bg-red-900/50 text-red-200 border border-red-900 hover:bg-red-900/70"
          >
            Revoke key
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}
